import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

const MEDIA_GALLERY = "media-gallery";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = event => {
    const source = event.detail.data.html;

    if (!source) {
      return;
    }
    const newMediaGallery = source.querySelector(MEDIA_GALLERY);

    if (!newMediaGallery) {
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {import('@theme/events').ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = event => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.#handleVariantUpdate), {
      signal,
    });
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      /** @type {EventListener} */ (this.#handleZoomMediaSelected),
      {
        signal,
      },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {Event} event - The activation event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get(MEDIA_GALLERY)) {
  customElements.define(MEDIA_GALLERY, MediaGallery);
}
